'use client'

import type { ChartOptions } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import '../shared/charts/chartSetup'
import { useEffect, useState } from 'react'

interface BarChartProps {
    data: {
        labels: string[]
        datasets: {
            label: string
            data: number[]
            backgroundColor: string
            hoverBackgroundColor: string
            barThickness: number
            maxBarThickness: number
            minBarLength: number
            borderRadius: number
        }[]
    }
}

export function BarChart({ data }: BarChartProps) {
    // Use state to track window width for responsive adjustments
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768)
        }

        // Check on initial render
        checkScreenSize()

        // Add resize listener
        window.addEventListener('resize', checkScreenSize)

        // Clean up
        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    const options: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        transitions: {
            active: { animation: { duration: 0 } }
        },
        // Fix overlapping bars
        indexAxis: 'x',
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: {
                    color: 'rgb(156, 163, 175)',
                    font: { size: isMobile ? 10 : 12 },
                    maxRotation: isMobile ? 0 : 0, // Keep labels horizontal
                    autoSkip: true,
                    autoSkipPadding: isMobile ? 15 : 5
                },
                // This is important to prevent bar overlap
                stacked: false
            },
            y: {
                grid: { color: 'rgba(156, 163, 175, 0.1)' },
                border: { display: false },
                ticks: {
                    color: 'rgb(156, 163, 175)',
                    font: { size: isMobile ? 10 : 12 },
                    // Round to one decimal place
                    callback: (value) => `${Number(value).toFixed(1)}%`,
                    count: isMobile ? 4 : 5, // Fewer ticks on mobile
                    maxTicksLimit: isMobile ? 4 : 5
                }
            }
        },
        // Important for bar spacing and positioning
        datasets: {
            bar: {
                categoryPercentage: isMobile ? 0.7 : 0.8, // Controls category width as a fraction of the available width
                barPercentage: isMobile ? 0.8 : 0.9,     // Controls bar width as a fraction of the category width
            }
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                align: 'center',
                labels: {
                    boxWidth: isMobile ? 6 : 8,
                    boxHeight: isMobile ? 6 : 8,
                    padding: isMobile ? 10 : 20,
                    color: 'rgb(156, 163, 175)',
                    font: { size: isMobile ? 10 : 12 },
                    usePointStyle: true,
                    pointStyle: 'circle'
                }
            },
            tooltip: {
                enabled: true,
                mode: 'nearest',
                intersect: true,
                position: 'nearest',
                yAlign: 'bottom',
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                titleColor: 'rgb(17, 24, 39)',
                titleFont: {
                    size: isMobile ? 11 : 13,
                    weight: 600,
                    family: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
                },
                bodyColor: 'rgb(55, 65, 81)',
                bodyFont: {
                    size: isMobile ? 10 : 12,
                    weight: 500,
                    family: '-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif'
                },
                padding: {
                    x: isMobile ? 8 : 12,
                    y: isMobile ? 6 : 8
                },
                cornerRadius: 8,
                displayColors: true,
                boxWidth: isMobile ? 4 : 6,
                boxHeight: isMobile ? 4 : 6,
                boxPadding: isMobile ? 2 : 4,
                usePointStyle: true,
                caretSize: isMobile ? 4 : 6,
                caretPadding: isMobile ? 6 : 8,
                borderColor: 'rgba(229, 231, 235, 1)',
                borderWidth: 1,
                callbacks: {
                    title: (tooltipItems) => {
                        return tooltipItems[0].label;
                    },
                    label: (context) => {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y;
                        return `${label}: ${value.toFixed(1)}%`;
                    }
                }
            },
        }
    }

    // Adjust bar thickness based on screen size
    const mobileData = {
        ...data,
        datasets: data.datasets.map(dataset => ({
            ...dataset,
            barThickness: isMobile ? 20 : 40, // Thinner bars on mobile
            maxBarThickness: isMobile ? 20 : 34
        }))
    }

    return (<Bar data={mobileData} options={options} />)
}